import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Building2, Shield, Save, Loader2, Users, UserCog, Plus, Trash2, Crown, Globe } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RestrictionLevel } from '../types';
import { companiesClient, requestsClient } from '../auth/keycloakProvider';
import { isWalletUnlocked } from '../auth/walletSession';
import { getEmployeesPaginated } from '../api-clients';
import { RegisterCompanyCommand, EmployeeDto, CompanyDto, SignAndSubmitBlockchainRequestCommand, EditCompanyCommand } from '../web-api-client';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';

export default function CompanyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isEditing = Boolean(id);
  
  // Get company and employees data from navigation state (when coming from CompanyDetail)
  const navigationState = location.state as { company?: CompanyDto; employees?: EmployeeDto[] } | null;
  const hasInitializedForm = useRef(false);
  const hasInitializedEmployees = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    vatNumber: '',
    address: '',
    city: '',
    country: 'Portugal',
    phone: '',
    email: '',
    selectedEmployeeId: 0,
    restrictionLevel: 0 as RestrictionLevel,
    adminId: 0 as number,
    webhookEnabled: false,
    webhookUrl: '',
    webhookSigningKey: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
    const [employees, setEmployees] = useState<EmployeeDto[]>([]);

    const [loadingEmployees, setLoadingEmployees] = useState(false);
    // Company employees management
    const [companyEmployees, setCompanyEmployees] = useState<EmployeeDto[]>([]);
    const [availableEmployees, setAvailableEmployees] = useState<EmployeeDto[]>([]);
    const [selectedEmployeesToAdd, setSelectedEmployeesToAdd] = useState<number[]>([]);

  // Initialize form data from navigation state (only once when editing)
  useEffect(() => {
    if (isEditing && navigationState?.company && !hasInitializedForm.current) {
      const company = navigationState.company;
      setFormData({
        name: company.companyName || '',
        vatNumber: company.vatNumber || '',
        address: '', // Not available in CompanyDto
        city: '', // Not available in CompanyDto
        country: 'Portugal', // Default
        phone: company.contactPhone || '',
        email: company.contactEmail || '',
        selectedEmployeeId: 0,
        restrictionLevel: 0 as RestrictionLevel, // Not available in CompanyDto
        adminId: 0 as number, // TODO: Get from company data if available
        webhookEnabled: company.webhookEnabled ?? false,
        webhookUrl: company.webhookUrl ?? '',
        webhookSigningKey: company.webhookSigningKey ?? '',
      });
      hasInitializedForm.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]); // Only depend on id and isEditing

  // Load employees and separate company employees
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
        try {
            const response = !isEditing ? await getEmployeesPaginated({ role: 'CompanyAdministrator', pageSize: 999 }) : await getEmployeesPaginated({ pageSize: 999 });
            const allEmployees = response.items || [];
            setEmployees(allEmployees);
        
            // If we have navigation state with company employees, separate them (only on initial load)
            if (isEditing && navigationState?.employees && navigationState?.company && !hasInitializedEmployees.current) {
              const companyEmployeeIds = new Set(navigationState.employees.map(e => e.id).filter((id): id is number => id !== undefined));
              // Use the employees from navigation state (they're the actual company employees)
              setCompanyEmployees(navigationState.employees);
              // Available employees are all employees minus the company employees
              const availableEmployeesData = allEmployees.filter(e => !e.id || !companyEmployeeIds.has(e.id));
              setAvailableEmployees(availableEmployeesData);
              hasInitializedEmployees.current = true;
            }
            else if (!isEditing || !navigationState?.employees || hasInitializedEmployees.current) {
              // No company employees yet, or already initialized - all employees are available
              // Only update if we haven't initialized or if we're not in edit mode
              if (!hasInitializedEmployees.current) {
                setAvailableEmployees(allEmployees);
              }
            }
      } catch (err) {
        console.error('Failed to load employees:', err);
        const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar colaboradores');
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]); // Only run when id or isEditing changes

    const handleAddEmployees = () => {
        if (selectedEmployeesToAdd.length === 0) {
            toast({
                title: 'Aviso',
                description: 'Selecione pelo menos um colaborador para adicionar',
                variant: 'destructive',
            });
            return;
        }

        const employeesToAdd = availableEmployees.filter((e) => selectedEmployeesToAdd.includes(e.id));
        setCompanyEmployees((prev) => [...prev, ...employeesToAdd]);
        setAvailableEmployees((prev) => prev.filter((e) => !selectedEmployeesToAdd.includes(e.id)));
        setSelectedEmployeesToAdd([]);
        //toast.success(`${employeesToAdd.length} colaborador(es) adicionado(s)`);
    };

    const handleRemoveEmployee = (employeeId: number) => {
        const employee = companyEmployees.find((e) => e.id === employeeId);
        if (employee) {
            setCompanyEmployees((prev) => prev.filter((e) => e.id !== employeeId));
            setAvailableEmployees((prev) => [...prev, employee]);

            // Clear admin if removed
            if (formData.adminId === employeeId) {
                setFormData((prev) => ({ ...prev, adminId: 0 }));
            }

            //toast.success('Colaborador removido');
        }
    };

    const toggleEmployeeSelection = useCallback((employeeId: number) => {
        setSelectedEmployeesToAdd((prev) =>
            prev.includes(employeeId)
                ? prev.filter((id) => id !== employeeId)
                : [...prev, employeeId]
        );
    }, []);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: field === 'selectedEmployeeId' ? (typeof value === 'string' ? parseInt(value, 10) || 0 : value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      if (!id) {
        toast({
          title: 'Erro',
          description: 'ID da empresa não encontrado',
          variant: 'destructive',
        });
        return;
      }

      setIsLoading(true);

        try {

            if (!isWalletUnlocked()) {
                toast({
                    title: 'Aviso',
                    description: 'Por favor, desbloqueie a wallet antes de continuar',
                    variant: 'destructive',
                });
                setIsLoading(false);
                return;
            }
        // Calculate which employees were added
        const initialEmployeeIds = new Set(
          (navigationState?.employees || []).map(e => e.id).filter((id): id is number => id !== undefined)
        );
        const currentEmployeeIds = companyEmployees
          .map(e => e.id)
          .filter((id): id is number => id !== undefined);
        const addedEmployeeIds = currentEmployeeIds.filter(id => !initialEmployeeIds.has(id));

        const command = new EditCompanyCommand({
          companyId: parseInt(id, 10),
          addedEmployeeIds: addedEmployeeIds,
          webhookEnabled: formData.webhookEnabled,
          webhookUrl: formData.webhookUrl || undefined,
          webhookSigningKey: formData.webhookSigningKey || undefined,
          restrictionLevel: formData.restrictionLevel || 0,
        });

        const response = await companiesClient.editCompany(parseInt(id, 10), command);

        // Sign and submit all blockchain requests
            setCompleting(true);
            const requestUUIDs = response.requestsUUID || [];
        if (requestUUIDs && requestUUIDs.length > 0) {
          try {
            for (const requestUUID of requestUUIDs) {
              if (requestUUID) {
                await requestsClient.signAndSubmitBlockchainRequest(new SignAndSubmitBlockchainRequestCommand({
                  requestUUID: requestUUID
                }));
              }
            }
          } catch (err) {
            const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar e submeter transações blockchain');
            toast({
              title: errorToast.title,
              description: errorToast.description,
              variant: 'destructive',
            });
            setCompleting(false);
            setIsLoading(false);
            return;
          }
        }
        
        toast({
          title: 'Sucesso',
          description: `Empresa "${formData.name}" atualizada com sucesso!`,
        });
        
        setTimeout(() => {
          navigate('/companies');
        }, 2000);
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Falha ao atualizar empresa');
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);

    try {
      const command = new RegisterCompanyCommand({
        vatNumber: formData.vatNumber,
        companyName: formData.name,
        contactEmail: formData.email,
        contactPhone: formData.phone || undefined,
        companyEmployeeAdminId: formData.selectedEmployeeId || undefined,
      });

      if (!formData.selectedEmployeeId) {
        toast({
          title: 'Erro',
          description: 'Por favor, selecione um colaborador administrador',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      try {
        const response = await companiesClient.registerCompany(command);
        
        setCompleting(true);
        await requestsClient.signAndSubmitBlockchainRequest(new SignAndSubmitBlockchainRequestCommand({
          requestUUID: response.requestUUID
        }));
        
        toast({
          title: 'Sucesso',
          description: `Empresa "${formData.name}" registada com sucesso!`,
        });
        
        setTimeout(() => {
          navigate('/companies');
        }, 2000);
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Falha ao completar o registo da empresa');
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setCompleting(false);
        setIsLoading(false);
      }
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao registar empresa');
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

    const roleLabels: Record<string, string> = {
        driver: 'Motorista',
        manager: 'Gestor',
        dispatcher: 'Despachante',
    };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Editar Empresa' : 'Nova Empresa'}
        description={isEditing ? 'Atualizar dados da empresa' : 'Registar uma nova empresa no sistema'}
        backLink="/companies"
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Informações da Empresa
              </CardTitle>
              <CardDescription>
                Dados gerais da empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="name">Nome da Empresa *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ex: TransLog Lda"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vatNumber">NIF / VAT *</Label>
                  <Input
                    id="vatNumber"
                    value={formData.vatNumber}
                    onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                    placeholder="Ex: PT123456789"
                    required
                    disabled={isEditing}
                  />
                  {isEditing && (
                    <p className="text-xs text-muted-foreground">
                      O NIF não pode ser alterado após o registo na blockchain
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Ex: +351 21 123 4567"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Ex: geral@empresa.pt"
                    required
                  />
                </div>
                {!isEditing && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="selectedEmployeeId">Colaborador Administrador *</Label>
                    <Select
                      value={formData.selectedEmployeeId.toString()}
                      onValueChange={(v) => handleInputChange('selectedEmployeeId', v)}
                      disabled={loadingEmployees}
                    >
                      <SelectTrigger id="selectedEmployeeId">
                        <SelectValue placeholder="Selecione um colaborador administrador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">-- Selecione um colaborador --</SelectItem>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id?.toString() || '0'}>
                            {employee.fullName} - {employee.email} {employee.walletAddress ? `(${employee.walletAddress.substring(0, 10)}...)` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {loadingEmployees && (
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        A carregar colaboradores...
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      O colaborador selecionado será o administrador da empresa
                    </p>
                  </div>
                )}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="address">Morada</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Ex: Rua Principal, 123"
                  />
                </div>
                {/*<div className="space-y-2">*/}
                {/*  <Label htmlFor="city">Cidade</Label>*/}
                {/*  <Input*/}
                {/*    id="city"*/}
                {/*    value={formData.city}*/}
                {/*    onChange={(e) => handleInputChange('city', e.target.value)}*/}
                {/*    placeholder="Ex: Lisboa"*/}
                {/*  />*/}
                {/*</div>*/}
                {/*<div className="space-y-2">*/}
                {/*  <Label htmlFor="country">País</Label>*/}
                {/*  <Select*/}
                {/*    value={formData.country}*/}
                {/*    onValueChange={(v) => handleInputChange('country', v)}*/}
                {/*  >*/}
                {/*    <SelectTrigger id="country">*/}
                {/*      <SelectValue />*/}
                {/*    </SelectTrigger>*/}
                {/*    <SelectContent>*/}
                {/*      <SelectItem value="Portugal">Portugal</SelectItem>*/}
                {/*      <SelectItem value="Espanha">Espanha</SelectItem>*/}
                {/*      <SelectItem value="França">França</SelectItem>*/}
                {/*      <SelectItem value="Alemanha">Alemanha</SelectItem>*/}
                {/*      <SelectItem value="Itália">Itália</SelectItem>*/}
                {/*    </SelectContent>*/}
                {/*  </Select>*/}
                {/*</div>*/}
              </div>

            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Permissões e Restrições
              </CardTitle>
              <CardDescription>
                Configure os acessos e limites da empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/*<div className="space-y-4">*/}
              {/*  <div className="flex items-center justify-between">*/}
              {/*    <div className="space-y-0.5">*/}
              {/*      <Label>Criar eCMRs</Label>*/}
              {/*      <p className="text-xs text-muted-foreground">*/}
              {/*        Permitir que a empresa crie novos documentos eCMR*/}
              {/*      </p>*/}
              {/*    </div>*/}
              {/*    <Switch*/}
              {/*      checked={permissions.canCreateEcmr}*/}
              {/*      onCheckedChange={(v) => handlePermissionChange('canCreateEcmr', v)}*/}
              {/*    />*/}
              {/*  </div>*/}

              {/*  <div className="flex items-center justify-between">*/}
              {/*    <div className="space-y-0.5">*/}
              {/*      <Label>Assinar eCMRs</Label>*/}
              {/*      <p className="text-xs text-muted-foreground">*/}
              {/*        Permitir assinatura digital de documentos*/}
              {/*      </p>*/}
              {/*    </div>*/}
              {/*    <Switch*/}
              {/*      checked={permissions.canSignEcmr}*/}
              {/*      onCheckedChange={(v) => handlePermissionChange('canSignEcmr', v)}*/}
              {/*    />*/}
              {/*  </div>*/}

              {/*  <div className="flex items-center justify-between">*/}
              {/*    <div className="space-y-0.5">*/}
              {/*      <Label>Ver todos os eCMRs</Label>*/}
              {/*      <p className="text-xs text-muted-foreground">*/}
              {/*        Acesso a todos os eCMRs onde a empresa participa*/}
              {/*      </p>*/}
              {/*    </div>*/}
              {/*    <Switch*/}
              {/*      checked={permissions.canViewAllEcmrs}*/}
              {/*      onCheckedChange={(v) => handlePermissionChange('canViewAllEcmrs', v)}*/}
              {/*    />*/}
              {/*  </div>*/}

              {/*  <div className="flex items-center justify-between">*/}
              {/*    <div className="space-y-0.5">*/}
              {/*      <Label>Gerir colaboradores</Label>*/}
              {/*      <p className="text-xs text-muted-foreground">*/}
              {/*        Adicionar e remover colaboradores da empresa*/}
              {/*      </p>*/}
              {/*    </div>*/}
              {/*    <Switch*/}
              {/*      checked={permissions.canManageEmployees}*/}
              {/*      onCheckedChange={(v) => handlePermissionChange('canManageEmployees', v)}*/}
              {/*    />*/}
              {/*    </div>*/}
              {/*</div>*/}

              <Separator />

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="restrictionLevel">Nível de Restrição</Label>
                  <Select
                    value={formData.restrictionLevel.toString()}
                    onValueChange={(v) => handleInputChange('restrictionLevel', parseInt(v) as RestrictionLevel)}
                  >
                    <SelectTrigger id="restrictionLevel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Nível 0 - Sem Restrição</SelectItem>
                      <SelectItem value="1">Nível 1 - Restrição Parcial</SelectItem>
                      <SelectItem value="2">Nível 2 - Restrito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Separator />
                
                <div className="space-y-3 text-sm">
                  <div className="p-3 rounded-lg bg-restriction-0-bg text-restriction-0-foreground">
                    <p className="font-medium">Nível 0 - Sem Restrição</p>
                    <p className="text-xs mt-1">Acesso completo a todas as funcionalidades permitidas</p>
                  </div>
                  <div className="p-3 rounded-lg bg-restriction-1-bg text-restriction-1-foreground">
                    <p className="font-medium">Nível 1 - Restrição Parcial</p>
                    <p className="text-xs mt-1">Pode visualizar mas não pode criar ou editar documentos</p>
                  </div>
                  <div className="p-3 rounded-lg bg-restriction-2-bg text-restriction-2-foreground">
                    <p className="font-medium">Nível 2 - Restrito</p>
                    <p className="text-xs mt-1">Acesso completamente bloqueado ao sistema</p>
                  </div>
                </div>
              </div>

              {/*<Separator />*/}

              {/*<div className="space-y-4">*/}
              {/*  <div className="space-y-2">*/}
              {/*    <Label htmlFor="maxEmployees">Limite de Colaboradores</Label>*/}
              {/*    <Input*/}
              {/*      id="maxEmployees"*/}
              {/*      type="number"*/}
              {/*      min="1"*/}
              {/*      value={permissions.maxEmployees}*/}
              {/*      onChange={(e) => handlePermissionChange('maxEmployees', parseInt(e.target.value) || 1)}*/}
              {/*    />*/}
              {/*    <p className="text-xs text-muted-foreground">*/}
              {/*      Número máximo de colaboradores que a empresa pode ter*/}
              {/*    </p>*/}
              {/*  </div>*/}

              {/*  <div className="space-y-2">*/}
              {/*    <Label htmlFor="maxEcmrs">Limite de eCMRs / Mês</Label>*/}
              {/*    <Input*/}
              {/*      id="maxEcmrs"*/}
              {/*      type="number"*/}
              {/*      min="1"*/}
              {/*      value={permissions.maxEcmrsPerMonth}*/}
              {/*      onChange={(e) => handlePermissionChange('maxEcmrsPerMonth', parseInt(e.target.value) || 1)}*/}
              {/*    />*/}
              {/*    <p className="text-xs text-muted-foreground">*/}
              {/*      Número máximo de eCMRs que podem ser criados por mês*/}
              {/*    </p>*/}
              {/*  </div>*/}
              {/*</div>*/}
            </CardContent>
          </Card>
        </div>


              {/* Employees Management - Only show in edit mode */}
              {isEditing && (
                  <Card>
                      <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                              <Users className="h-5 w-5" />
                              Gestão de Colaboradores
                          </CardTitle>
                          <CardDescription>
                              Adicione ou remova colaboradores desta empresa
                          </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                          {/* Admin Selection */}
                          <div className="space-y-2">
                              <Label htmlFor="adminId" className="flex items-center gap-2">
                                  <UserCog className="h-4 w-4" />
                                  Administrador da Empresa
                              </Label>
                              <Select
                                  value={formData.adminId.toString()}
                                  onValueChange={(v) => handleInputChange('adminId', v)}
                              >
                                  <SelectTrigger id="adminId">
                                      <SelectValue placeholder="Selecione o administrador" />
                                  </SelectTrigger>
                                  <SelectContent>
                                      {companyEmployees.filter(e => e.id).map((employee) => (
                                          <SelectItem key={employee.id} value={employee.id!.toString()}>
                                              <div className="flex items-center gap-2">
                                                  <span>{employee.fullName}</span>
                                                  {(employee as any).role && (
                                                      <Badge variant="secondary" className="text-xs">
                                                          {roleLabels[(employee as any).role] || (employee as any).role}
                                                      </Badge>
                                                  )}
                                              </div>
                                          </SelectItem>
                                      ))}
                                  </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                  O administrador tem acesso completo à gestão da empresa
                              </p>
                          </div>

                          <Separator />

                          {/* Current Employees */}
                          <div className="space-y-3">
                              <Label>Colaboradores Atuais ({companyEmployees.length})</Label>
                              {companyEmployees.length === 0 ? (
                                  <p className="text-sm text-muted-foreground py-4 text-center">
                                      Nenhum colaborador associado a esta empresa
                                  </p>
                              ) : (
                                  <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {companyEmployees.filter(e => e.id).map((employee) => (
                                          <div
                                              key={employee.id}
                                              className="flex items-center justify-between p-3 rounded-lg border bg-card"
                                          >
                                              <div className="flex items-center gap-3">
                                                  {formData.adminId === employee.id && (
                                                      <Crown className="h-4 w-4 text-yellow-500" />
                                                  )}
                                                  <div>
                                                      <p className="font-medium">{employee.fullName}</p>
                                                      <p className="text-sm text-muted-foreground">{employee.email}</p>
                                                  </div>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                  {(employee as any).role && (
                                                      <Badge variant="secondary">
                                                          {roleLabels[(employee as any).role] || (employee as any).role}
                                                      </Badge>
                                                  )}
                                                  <Button
                                                      type="button"
                                                      variant="ghost"
                                                      size="icon"
                                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                                      onClick={() => employee.id && handleRemoveEmployee(employee.id)}
                                                  >
                                                      <Trash2 className="h-4 w-4" />
                                                  </Button>
                                              </div>
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>

                          <Separator />

                          {/* Add Employees */}
                          <div className="space-y-3">
                              <Label>Adicionar Colaboradores</Label>
                              {availableEmployees.length === 0 ? (
                                  <p className="text-sm text-muted-foreground py-4 text-center">
                                      Não há colaboradores disponíveis para adicionar
                                  </p>
                              ) : (
                                  <>
                                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                          {availableEmployees.filter(e => e.id).map((employee) => {
                                              const employeeId = employee.id!;
                                              const isSelected = selectedEmployeesToAdd.includes(employeeId);
                                              return (
                                                  <div
                                                      key={employeeId}
                                                      className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
                                                  >
                                                      <Checkbox
                                                          checked={isSelected}
                                                          onCheckedChange={() => {
                                                            toggleEmployeeSelection(employeeId);
                                                          }}
                                                      />
                                                  <div className="flex-1 cursor-pointer" onClick={() => toggleEmployeeSelection(employeeId)}>
                                                      <p className="font-medium">{employee.fullName}</p>
                                                      <p className="text-sm text-muted-foreground">{employee.email}</p>
                                                  </div>
                                                  {(employee as any).role && (
                                                      <Badge variant="secondary">
                                                          {roleLabels[(employee as any).role] || (employee as any).role}
                                                      </Badge>
                                                  )}
                                              </div>
                                              );
                                          })}
                                      </div>
                                      <Button
                                          type="button"
                                          variant="outline"
                                          onClick={handleAddEmployees}
                                          disabled={selectedEmployeesToAdd.length === 0}
                                          className="w-full"
                                      >
                                          <Plus className="h-4 w-4 mr-2" />
                                          Adicionar {selectedEmployeesToAdd.length > 0 ? `(${selectedEmployeesToAdd.length})` : ''} Colaborador(es)
                                      </Button>
                                  </>
                              )}
                          </div>
                      </CardContent>
                  </Card>
              )}

        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Configuração de Webhooks
              </CardTitle>
              <CardDescription>
                Receba notificações automáticas quando eventos ocorrerem
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Webhooks ativos</Label>
                  <p className="text-xs text-muted-foreground">
                    Ativar envio de notificações para a URL configurada
                  </p>
                </div>
                <Switch
                  checked={formData.webhookEnabled}
                  onCheckedChange={(v) => handleInputChange('webhookEnabled', v)}
                />
              </div>

              {formData.webhookEnabled && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <Label htmlFor="webhookUrl">URL do Webhook</Label>
                    <Input
                      id="webhookUrl"
                      type="url"
                      value={formData.webhookUrl}
                      onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
                      placeholder="https://exemplo.com/webhook"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="webhookSigningKey">Chave de Assinatura</Label>
                    <Input
                      id="webhookSigningKey"
                      type="password"
                      value={formData.webhookSigningKey}
                      onChange={(e) => handleInputChange('webhookSigningKey', e.target.value)}
                      placeholder="Chave secreta HS256"
                    />
                    <p className="text-xs text-muted-foreground">
                      Utilizada para assinar o JWT enviado com cada evento
                    </p>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <Label>Eventos disponíveis</Label>
                    <div className="p-3 rounded-lg border bg-muted/30 text-sm space-y-1">
                      <p className="font-medium font-mono">transport_note.created</p>
                      <p className="text-xs text-muted-foreground">
                        Disparado quando uma nota de transporte de severidade Alta ou Crítica é criada
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/companies')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading || completing}>
            {isLoading || completing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {completing ? 'A completar...' : 'A guardar...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Guardar Alterações' : 'Criar Empresa'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
